import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Alert, Container, Button } from 'react-bootstrap';
import { ShieldX, Home, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useBoutique } from '../../contexts/BoutiqueContext';
import LoadingSpinner from './LoadingSpinner';

const ProtectedRoute = ({ children, roles = [], requiresBoutique = false }) => {
  const { user, loading, isAuthenticated, hasRole } = useAuth();
  const { currentBoutique, loading: boutiqueLoading } = useBoutique();
  const location = useLocation();

  // Affichage du loader pendant la vérification
  if (loading || boutiqueLoading) {
    return <LoadingSpinner message="Vérification des permissions..." />;
  }

  // Redirection vers login si non authentifié
  if (!isAuthenticated) {
    return (
      <Navigate 
        to="/login" 
        state={{ from: location }} 
        replace 
      />
    );
  }

  // Vérification des rôles requis
  if (roles.length > 0 && !hasRole(roles)) {
    return <UnauthorizedAccess userRole={user?.role} requiredRoles={roles} />;
  }

  // Vérification si une boutique est requise
  if (requiresBoutique && !currentBoutique) {
    return <NoBoutiqueAccess userRole={user?.role} />;
  }

  // ✅ CORRIGÉ: Vérification d'accès à la boutique pour les non-super-admins
  if (currentBoutique && user?.role !== 'SUPER_ADMIN') {
    // Récupérer l'ID de boutique de l'utilisateur (plusieurs formats possibles)
    const userBoutiqueId = user?.boutique?.id || user?.boutique_id || user?.boutiqueId;
    
    if (userBoutiqueId && userBoutiqueId !== currentBoutique.id) {
      console.warn('❌ Accès refusé - boutique non autorisée');
      return <BoutiqueAccessDenied />;
    }
    
    if (!userBoutiqueId) {
      console.warn('❌ Utilisateur sans boutique assignée');
      return <NoBoutiqueAccess userRole={user?.role} />;
    }
    
  }

  // Affichage du contenu si toutes les vérifications passent
  return children;
};

// Composant pour accès non autorisé
const UnauthorizedAccess = ({ userRole, requiredRoles }) => {
  const getRoleLabel = (role) => {
    switch (role) {
      case 'SUPER_ADMIN': return 'Super Administrateur';
      case 'MANAGER': return 'Gestionnaire';
      case 'EMPLOYEE': return 'Employé';
      default: return 'Utilisateur';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'SUPER_ADMIN': return '👑';
      case 'MANAGER': return '🏪';
      case 'EMPLOYEE': return '👤';
      default: return '🔒';
    }
  };

  return (
    <Container className="py-5">
      <div className="text-center">
        <div className="unauthorized-icon mb-4">
          <ShieldX size={80} className="text-danger" />
        </div>
        
        <h2 className="fw-bold text-dark mb-3">
          Accès non autorisé
        </h2>
        
        <Alert variant="warning" className="mx-auto" style={{ maxWidth: '500px' }}>
          <div className="d-flex align-items-center justify-content-center mb-2">
            <span className="me-2">{getRoleIcon(userRole)}</span>
            <strong>Votre rôle : {getRoleLabel(userRole)}</strong>
          </div>
          <hr />
          <div>
            <strong>Rôles requis :</strong>
            <div className="mt-2">
              {requiredRoles.map(role => (
                <span key={role} className="badge bg-primary me-2">
                  {getRoleIcon(role)} {getRoleLabel(role)}
                </span>
              ))}
            </div>
          </div>
        </Alert>
        
        <p className="text-muted mb-4">
          Vous n'avez pas les permissions nécessaires pour accéder à cette page.
          <br />
          Contactez votre administrateur si vous pensez qu'il s'agit d'une erreur.
        </p>
        
        <div className="d-flex gap-2 justify-content-center">
          <Button variant="outline-secondary" onClick={() => window.history.back()}>
            <ArrowLeft size={16} className="me-2" />
            Retour
          </Button>
          <Button variant="primary" href="/">
            <Home size={16} className="me-2" />
            Accueil
          </Button>
        </div>
      </div>

      <style jsx>{`
        .unauthorized-icon {
          animation: shake 0.8s ease-in-out;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }

        .badge {
          font-size: 12px;
        }
      `}</style>
    </Container>
  );
};

// Composant pour absence de boutique
const NoBoutiqueAccess = ({ userRole }) => {
  return (
    <Container className="py-5">
      <div className="text-center">
        <div className="mb-4">
          <span style={{ fontSize: '80px' }}>🏪</span>
        </div>
        
        <h2 className="fw-bold text-dark mb-3">
          Aucune boutique sélectionnée
        </h2>
        
        <Alert variant="info" className="mx-auto" style={{ maxWidth: '500px' }}>
          {userRole === 'SUPER_ADMIN' ? (
            <>
              <strong>Super Administrateur</strong>
              <hr />
              Vous devez sélectionner une boutique pour accéder à cette fonctionnalité.
              Utilisez le sélecteur de boutique dans la barre de navigation.
            </>
          ) : (
            <>
              <strong>Boutique non assignée</strong>
              <hr />
              Votre compte n'est pas associé à une boutique.
              Contactez votre administrateur pour résoudre ce problème.
            </>
          )}
        </Alert>
        
        <div className="d-flex gap-2 justify-content-center">
          <Button variant="outline-secondary" onClick={() => window.history.back()}>
            <ArrowLeft size={16} className="me-2" />
            Retour
          </Button>
          <Button variant="primary" href="/">
            <Home size={16} className="me-2" />
            Accueil
          </Button>
        </div>
      </div>
    </Container>
  );
};

// ✅ NOUVEAU: Composant pour accès boutique refusé avec debug
const BoutiqueAccessDenied = () => {
  const { user } = useAuth();
  const { currentBoutique } = useBoutique();
  
  return (
    <Container className="py-5">
      <div className="text-center">
        <div className="mb-4">
          <ShieldX size={80} className="text-warning" />
        </div>
        
        <h2 className="fw-bold text-dark mb-3">
          Accès à la boutique refusé
        </h2>
        
        <Alert variant="warning" className="mx-auto" style={{ maxWidth: '600px' }}>
          <strong>Boutique non autorisée</strong>
          <hr />
          <p>Vous n'avez pas accès à cette boutique.</p>
          <p>Vous ne pouvez accéder qu'aux données de votre boutique assignée.</p>
          
          {/* Debug info pour le développement */}
          {process.env.NODE_ENV === 'development' && (
            <details className="mt-3">
              <summary className="cursor-pointer text-primary">
                <small>🔍 Informations de debug (développement)</small>
              </summary>
              <div className="mt-2 text-start">
                <small>
                  <strong>Boutique courante:</strong><br />
                  - ID: {currentBoutique?.id}<br />
                  - Nom: {currentBoutique?.name}<br />
                  <br />
                  <strong>Utilisateur:</strong><br />
                  - boutique_id: {user?.boutique_id}<br />
                  - boutiqueId: {user?.boutiqueId}<br />
                  - boutique.id: {user?.boutique?.id}<br />
                  - Role: {user?.role}<br />
                </small>
              </div>
            </details>
          )}
        </Alert>
        
        <div className="d-flex gap-2 justify-content-center">
          <Button variant="outline-secondary" onClick={() => window.history.back()}>
            <ArrowLeft size={16} className="me-2" />
            Retour
          </Button>
          <Button variant="primary" href="/">
            <Home size={16} className="me-2" />
            Accueil
          </Button>
        </div>
      </div>
    </Container>
  );
};

export default ProtectedRoute;